import { useRef, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Mailbox, ResponderRule, ResponseMode } from '@mailpilot/shared';
import { DEFAULT_ACK_TEMPLATE, emptyConditions } from '@mailpilot/shared';
import { AlertTriangle, ShieldCheck } from 'lucide-react';
import { api } from '../../lib/api';
import { qk } from '../../lib/keys';
import { Badge, Button, Checkbox, ErrorState, Field, InlineError, Input, Modal, Notice, Segmented, Select, SkeletonLines, Switch, Textarea, useToast } from '../ui';
import { SubSection, Well } from './SettingsPrimitives';
import { ResponderConditions } from './ResponderConditions';
import { MODE_META } from './ResponderShared';

type RuleDraft = Omit<ResponderRule, 'id' | 'createdAt' | 'updatedAt'>;

function newRule(timezone: string, priority: number): RuleDraft {
  const conditions = emptyConditions();
  conditions.businessHours.timezone = timezone;
  return {
    mailboxId: null,
    name: '',
    enabled: true,
    mode: 'suggest',
    priority,
    conditions,
    ackTemplate: DEFAULT_ACK_TEMPLATE,
    autoReplyInstructions: '',
    tone: '',
    maxRepliesPerSenderPerDay: 1,
    cooldownMinutes: 60,
    requireApproval: true,
    allowedAttachmentIds: [],
  };
}

function fromRule(r: ResponderRule): RuleDraft {
  const { id: _id, createdAt: _c, updatedAt: _u, ...rest } = r;
  return { ...rest, ackTemplate: rest.ackTemplate ?? DEFAULT_ACK_TEMPLATE, autoReplyInstructions: rest.autoReplyInstructions ?? '', tone: rest.tone ?? '' };
}

const PLACEHOLDERS = [
  { token: '{{sender_name}}', label: 'Sender name' },
  { token: '{{subject}}', label: 'Subject' },
  { token: '{{my_name}}', label: 'My name' },
];

export function ResponderRuleEditor({
  open,
  onClose,
  rule,
  mailboxes,
  timezone,
  nextPriority,
}: {
  open: boolean;
  onClose: () => void;
  rule: ResponderRule | null;
  mailboxes: Mailbox[];
  timezone: string;
  nextPriority: number;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const [d, setD] = useState<RuleDraft>(() => (rule ? fromRule(rule) : newRule(timezone, nextPriority)));
  const [touched, setTouched] = useState(false);
  const ackRef = useRef<HTMLTextAreaElement>(null);
  const set = <K extends keyof RuleDraft>(k: K, v: RuleDraft[K]) => setD((p) => ({ ...p, [k]: v }));

  const library = useQuery({ queryKey: qk.library, queryFn: () => api('GET /api/library'), enabled: open && d.mode === 'auto_reply' });

  const save = useMutation({
    mutationFn: (body: RuleDraft) => {
      const clean: Partial<ResponderRule> = {
        ...body,
        name: body.name.trim(),
        ackTemplate: body.mode === 'acknowledge' ? body.ackTemplate : body.ackTemplate || null,
        autoReplyInstructions: body.autoReplyInstructions?.trim() || null,
        tone: body.tone?.trim() || null,
      };
      return rule ? api('PATCH /api/rules/:id', { params: { id: rule.id }, body: clean }) : api('POST /api/rules', { body: clean });
    },
    onSuccess: (res) => {
      void qc.invalidateQueries({ queryKey: qk.rules });
      toast.success(rule ? `Rule "${res.rule.name}" saved` : `Rule "${res.rule.name}" created`);
      onClose();
    },
  });

  const insertPlaceholder = (token: string) => {
    const el = ackRef.current;
    const cur = d.ackTemplate ?? '';
    if (!el) {
      set('ackTemplate', cur + token);
      return;
    }
    const start = el.selectionStart ?? cur.length;
    const end = el.selectionEnd ?? cur.length;
    const next = cur.slice(0, start) + token + cur.slice(end);
    set('ackTemplate', next);
    requestAnimationFrame(() => {
      el.focus();
      const pos = start + token.length;
      el.setSelectionRange(pos, pos);
    });
  };

  const errors = {
    name: !d.name.trim() ? 'Give the rule a name' : undefined,
    ack: d.mode === 'acknowledge' && !d.ackTemplate?.trim() ? 'An acknowledgement needs a template' : undefined,
    instructions: d.mode === 'auto_reply' && !d.autoReplyInstructions?.trim() ? 'Automatic replies need the facts the AI may use' : undefined,
  };
  const invalid = Object.values(errors).some(Boolean);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (!invalid) save.mutate(d);
  };

  const approvedFiles = (library.data?.files ?? []).filter((f) => f.approved && !f.supersededById);
  const toggleFile = (id: string, on: boolean) => set('allowedAttachmentIds', on ? [...d.allowedAttachmentIds, id] : d.allowedAttachmentIds.filter((x) => x !== id));

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="xl"
      title={rule ? `Edit rule: ${rule.name}` : 'New responder rule'}
      description="Rules run in priority order; the first match wins. Safety checks (no-reply, mailing lists, bounces, global pause…) always apply first."
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" type="submit" form="rule-form" loading={save.isPending}>
            {rule ? 'Save rule' : 'Create rule'}
          </Button>
        </>
      }
    >
      <form id="rule-form" onSubmit={submit} className="space-y-6">
        {/* Basics */}
        <section className="space-y-5">
          <div className="grid gap-5 sm:grid-cols-2">
            <Field label="Rule name" required error={touched ? errors.name : undefined}>
              {(id) => <Input id={id} value={d.name} onChange={(e) => set('name', e.target.value)} placeholder="e.g. Support acknowledgements" />}
            </Field>
            <Field label="Applies to">
              {(id) => (
                <Select id={id} value={d.mailboxId ?? ''} onChange={(e) => set('mailboxId', e.target.value || null)}>
                  <option value="">All mailboxes</option>
                  {mailboxes.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.displayName ? `${m.displayName} (${m.email})` : m.email}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
          </div>
          <div className="space-y-2">
            <p className="text-[0.8125rem] font-medium text-ink">Response mode</p>
            <Segmented<ResponseMode>
              label="Response mode"
              value={d.mode}
              onChange={(v) => set('mode', v)}
              options={(Object.keys(MODE_META) as ResponseMode[]).map((m) => ({ value: m, label: MODE_META[m].label }))}
            />
            <p className="text-[0.8125rem] leading-relaxed text-muted">{MODE_META[d.mode].explain}</p>
          </div>
          <div className="grid gap-5 sm:grid-cols-2">
            <Field label="Priority" hint="Lower numbers run first.">
              {(id) => <Input id={id} type="number" min={0} value={d.priority} onChange={(e) => set('priority', Number(e.target.value) || 0)} />}
            </Field>
            <div className="flex items-start pt-7">
              <div className="w-full">
                <Switch id="rule-enabled" checked={d.enabled} onChange={(v) => set('enabled', v)} label="Rule enabled" description={d.enabled ? 'Runs on new mail.' : 'Saved but inactive.'} />
              </div>
            </div>
          </div>
        </section>

        <ResponderConditions value={d.conditions} onChange={(c) => set('conditions', c)} />

        {/* Acknowledgement */}
        {d.mode === 'acknowledge' && (
          <section className="border-t border-line/70 pt-6">
          <SubSection title="Acknowledgement template" hint="Sent once to the sender while the real reply waits for your review.">
            <Field label="Message sent once to the sender" required error={touched ? errors.ack : undefined}>
              {(id) => <Textarea id={id} ref={ackRef} rows={6} value={d.ackTemplate ?? ''} onChange={(e) => set('ackTemplate', e.target.value)} />}
            </Field>
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs text-muted">Insert:</span>
              {PLACEHOLDERS.map((p) => (
                <button
                  key={p.token}
                  type="button"
                  onClick={() => insertPlaceholder(p.token)}
                  className="rounded-lg bg-sunken px-2 py-1 font-mono text-xs text-ink transition-colors hover:bg-primary-soft hover:text-primary-ink"
                  aria-label={`Insert ${p.label} placeholder`}
                >
                  {p.token}
                </button>
              ))}
            </div>
            <Notice tone="warn" icon={<AlertTriangle className="h-4 w-4" aria-hidden />}>
              Don't promise a response time you can't keep — MailPilot never invents one.
            </Notice>
          </SubSection>
          </section>
        )}

        {/* Auto reply */}
        {d.mode === 'auto_reply' && (
          <section className="border-t border-line/70 pt-6">
          <SubSection title="Automatic reply" hint="The AI writes and sends under this rule, using only what you give it here.">
            <Field
              label="Instructions & facts the AI may use"
              required
              error={touched ? errors.instructions : undefined}
              hint="The AI may only use these facts. It never invents prices, commitments, dates or file contents — anything not covered here is left for you."
            >
              {(id) => (
                <Textarea
                  id={id}
                  rows={6}
                  value={d.autoReplyInstructions ?? ''}
                  onChange={(e) => set('autoReplyInstructions', e.target.value)}
                  placeholder="Opening hours are 9–5 Mon–Fri. Our returns address is…"
                />
              )}
            </Field>
            <Checkbox
              checked={d.requireApproval}
              onChange={(v) => set('requireApproval', v)}
              label="Require my approval before sending"
              description="Recommended. Replies wait in AI Review until you approve them."
            />
            <AttachmentPicker
              loading={library.isLoading}
              error={library.error}
              onRetry={() => void library.refetch()}
              files={approvedFiles}
              selected={d.allowedAttachmentIds}
              onToggle={toggleFile}
            />
          </SubSection>
          </section>
        )}

        {/* Limits */}
        <section className="border-t border-line/70 pt-6">
        <SubSection title="Tone & limits" hint="Guard-rails that apply to every send made by this rule.">
          <div className="grid gap-5 sm:grid-cols-3">
            <Field label="Tone" hint="e.g. friendly, formal">
              {(id) => <Input id={id} value={d.tone ?? ''} onChange={(e) => set('tone', e.target.value)} />}
            </Field>
            <Field label="Max replies per sender per day">
              {(id) => <Input id={id} type="number" min={1} value={d.maxRepliesPerSenderPerDay} onChange={(e) => set('maxRepliesPerSenderPerDay', Math.max(1, Number(e.target.value) || 1))} />}
            </Field>
            <Field label="Cooldown (minutes)" hint="Minimum gap between automatic sends to the same sender.">
              {(id) => <Input id={id} type="number" min={0} value={d.cooldownMinutes} onChange={(e) => set('cooldownMinutes', Math.max(0, Number(e.target.value) || 0))} />}
            </Field>
          </div>
        </SubSection>
        </section>

        <InlineError error={save.error} />
      </form>
    </Modal>
  );
}

function AttachmentPicker({
  loading,
  error,
  onRetry,
  files,
  selected,
  onToggle,
}: {
  loading: boolean;
  error: unknown;
  onRetry: () => void;
  files: { id: string; friendlyName: string; version: number; autoSendAllowed: boolean; sensitive: boolean }[];
  selected: string[];
  onToggle: (id: string, on: boolean) => void;
}) {
  return (
    <Well className="space-y-3">
      <div className="flex items-start gap-2.5">
        <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-surface text-primary-ink">
          <ShieldCheck className="h-4 w-4" aria-hidden />
        </span>
        <div>
          <p className="text-[0.8125rem] font-semibold text-ink">Library files this rule may send</p>
          <p className="text-xs leading-relaxed text-muted">A file is only sent automatically when both this rule and the file's own permission (in the Attachment Library) allow it.</p>
        </div>
      </div>
      {loading ? (
        <SkeletonLines lines={2} />
      ) : error ? (
        <ErrorState compact error={error} onRetry={onRetry} title="Couldn't load the library" />
      ) : files.length === 0 ? (
        <p className="text-[0.8125rem] text-muted">No approved files in your Attachment Library yet.</p>
      ) : (
        <div className="grid gap-2 sm:grid-cols-2">
          {files.map((f) => (
            <Checkbox
              key={f.id}
              checked={selected.includes(f.id)}
              onChange={(v) => onToggle(f.id, v)}
              label={
                <span className="inline-flex flex-wrap items-center gap-1.5">
                  {f.friendlyName} <Badge>v{f.version}</Badge>
                  {f.sensitive && <Badge tone="danger">Sensitive</Badge>}
                </span>
              }
              description={f.autoSendAllowed ? 'File allows automatic sending' : 'File does not allow automatic sending yet — change it in the library'}
            />
          ))}
        </div>
      )}
    </Well>
  );
}
